import React, { useEffect, useRef, useState } from 'react';
import { Pill, Trigger, fmtIST, intOrNull, num } from './shared';

/**
 * When each MEMAC trigger next sends on its own (backend: memac.schedule,
 * GET email/memac_schedule — reads only; owner, 2026-10-25: "a countdown timer
 * showing when it will be triggered if enabled").
 *
 * The server works out the scheduler run at which someone actually becomes due
 * (monthly day, days after sign-up / trial / payment, renewal −N days, a
 * period, or "who is due now" for event triggers), inside the send window,
 * and counts who would get it then with the dry run's own rules. The
 * countdown here is LIVE only when every gate is open (server env gate, global
 * switch, trigger On, mode Auto, an e-mail to send); otherwise it reads
 * "if enabled", muted, with the gates that block it.
 */

export type Gate = { key: string; label: string; open: boolean; closed: string };
export type Schedule = {
  trigger_id: string; kind: string; when: string;
  next_at: string | null; next_tick_at: string | null;
  due_count: number | null; due_count_exact: boolean; if_new_count: number | null; conditional: boolean;
  basis: string; note: string; runs_needed: number | null;
  gates: Gate[]; blocked_by: string[]; auto_possible: boolean; never_reason: string; live: boolean;
  estimated_ticks: boolean;
};
export type Schedules = {
  byId: Record<string, Schedule>;
  /** Server clock − this browser's clock, so every countdown reads the server's time. */
  offsetMs: number;
  envAuto: boolean | null; globalEnabled: boolean | null;
  scheduler: { running: boolean; next_run_at: string | null; estimated: boolean; interval: number | null;
    window: [number, number] | null; startup_delay: number | null };
  loading: boolean; error: string;
};

export const EMPTY_SCHEDULES: Schedules = {
  byId: {}, offsetMs: 0, envAuto: null, globalEnabled: null,
  scheduler: { running: false, next_run_at: null, estimated: true, interval: null, window: null, startup_delay: null },
  loading: false, error: '',
};

const str = (v: any) => (v === null || v === undefined ? '' : String(v));

export const normSchedule = (s: any): Schedule => ({
  trigger_id: str(s?.trigger_id),
  kind: str(s?.kind),
  when: str(s?.when),
  next_at: s?.next_at || null,
  next_tick_at: s?.next_tick_at || null,
  due_count: intOrNull(s?.due_count),
  due_count_exact: s?.due_count_exact === true,
  if_new_count: intOrNull(s?.if_new_count),
  conditional: s?.conditional === true,
  basis: str(s?.basis),
  note: str(s?.note),
  runs_needed: intOrNull(s?.runs_needed),
  gates: (Array.isArray(s?.gates) ? s.gates : []).map((g: any) => ({
    key: str(g?.key), label: str(g?.label), open: g?.open === true, closed: str(g?.closed),
  })),
  blocked_by: Array.isArray(s?.blocked_by) ? s.blocked_by.map(String) : [],
  auto_possible: s?.auto_possible === true,
  never_reason: str(s?.never_reason),
  // Only an explicit true is live: anything missing reads as "if enabled".
  live: s?.live === true,
  estimated_ticks: s?.estimated_ticks !== false,
});

export const normSchedules = (d: any, loading: boolean, error: string): Schedules => {
  if (!d) return { ...EMPTY_SCHEDULES, loading, error };
  const server = Date.parse(String(d.generated_at || ''));
  const byId: Record<string, Schedule> = {};
  Object.entries(d.schedules && typeof d.schedules === 'object' ? d.schedules : {})
    .forEach(([id, s]) => { byId[id] = normSchedule(s); });
  const w = Array.isArray(d.settings?.send_window_ist) ? d.settings.send_window_ist : null;
  return {
    byId,
    offsetMs: Number.isFinite(server) ? server - Date.now() : 0,
    envAuto: typeof d.env_auto === 'boolean' ? d.env_auto : null,
    globalEnabled: typeof d.global_enabled === 'boolean' ? d.global_enabled : null,
    scheduler: {
      running: d.scheduler?.running === true,
      next_run_at: d.scheduler?.next_run_at || null,
      estimated: d.scheduler?.estimated !== false,
      interval: intOrNull(d.settings?.interval_minutes),
      window: w && w.length === 2 ? [Number(w[0]), Number(w[1])] : null,
      startup_delay: intOrNull(d.settings?.startup_delay_minutes),
    },
    loading, error,
  };
};

// ---------------------------------------------------------------------------
// The countdown
// ---------------------------------------------------------------------------
const pad = (n: number) => String(n).padStart(2, '0');

/** "2d 04:10:22" (d hh:mm:ss). */
export const fmtCountdown = (ms: number): string => {
  const s = Math.max(0, Math.floor(ms / 1000));
  return `${Math.floor(s / 86400)}d ${pad(Math.floor((s % 86400) / 3600))}:${pad(Math.floor((s % 3600) / 60))}:${pad(s % 60)}`;
};

/** Re-renders every second. */
export const useTick = (ms = 1000) => {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), ms);
    return () => window.clearInterval(id);
  }, [ms]);
  return now;
};

export const Countdown: React.FC<{
  at: string; offsetMs: number; onElapsed?: () => void; className?: string;
}> = ({ at, offsetMs, onElapsed, className }) => {
  const now = useTick() + offsetMs;
  const left = Date.parse(at) - now;
  const fired = useRef('');
  useEffect(() => {
    if (left <= 0 && fired.current !== at) { fired.current = at; onElapsed?.(); }
  }, [left <= 0, at]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!Number.isFinite(left)) return <span className={className}>—</span>;
  return (
    <span className={`font-mono tabular-nums ${className || ''}`}>
      {left > 0 ? fmtCountdown(left) : 'due now'}
    </span>
  );
};

const peopleText = (s: Schedule): string => {
  if (s.conditional) return s.if_new_count !== null ? `${num(s.if_new_count)} if new data arrives` : 'if new data arrives';
  if (s.due_count === null) return '';
  return `${s.due_count_exact ? '' : '~'}${num(s.due_count)} ${s.due_count === 1 ? 'person' : 'people'}`;
};

/** Why it does not send on its own, one line (the gates that are closed). */
export const blockedText = (s: Schedule): string =>
  s.blocked_by.length ? `Blocked: ${s.blocked_by.join('; ')}.` : '';

// ---------------------------------------------------------------------------
// The table cell
// ---------------------------------------------------------------------------
export const NextSendCell: React.FC<{
  s?: Schedule; offsetMs: number; loading?: boolean; onElapsed?: () => void;
}> = ({ s, offsetMs, loading, onElapsed }) => {
  if (!s) return <span className="text-[11px] text-grayscale-900">{loading ? 'working out…' : '—'}</span>;
  if (s.kind === 'external' || s.kind === 'alias') {
    return <span className="text-[11px] text-grayscale-1100" title={s.when}>—</span>;
  }
  if (!s.auto_possible) {
    return (
      <span className="text-[11px] text-grayscale-1100" title={s.never_reason}>
        {s.kind === 'on_hold' ? 'on hold' : s.kind === 'broadcast' ? 'by hand only' : 'never automatic'}
      </span>
    );
  }
  const title = [s.live ? 'All gates are open: it sends on its own at this run.' : blockedText(s),
    s.note, s.estimated_ticks ? 'Run times estimated: the scheduler is not running on this server.' : '']
    .filter(Boolean).join('\n');
  if (!s.next_at) {
    return (
      <div className="text-[11px] text-grayscale-1100 leading-tight" title={title}>
        <div>{s.basis === 'event' ? 'when someone does it' : 'nobody due'}</div>
        {s.next_tick_at && (
          <div className="text-grayscale-900">next check <Countdown at={s.next_tick_at} offsetMs={offsetMs} /></div>
        )}
      </div>
    );
  }
  return (
    <div className={`text-[11px] leading-tight ${s.live ? 'text-red-700' : 'text-grayscale-1000'}`} title={title}>
      <div className="whitespace-nowrap">
        {s.live ? <b>sends in </b> : <span className="italic">if enabled: </span>}
        <Countdown at={s.next_at} offsetMs={offsetMs} onElapsed={onElapsed}
          className={s.live ? 'font-bold' : 'text-grayscale-1100'} />
      </div>
      <div className={s.live ? 'text-red-700' : 'text-grayscale-900'}>{fmtIST(s.next_at)} IST</div>
      {(peopleText(s) || s.conditional) && (
        <div className={s.live ? '' : 'text-grayscale-900'}>
          {peopleText(s)}
        </div>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// The full panel (a trigger's page, the cohort preview)
// ---------------------------------------------------------------------------
export const NextSendPanel: React.FC<{
  s?: Schedule; sch: Schedules; onElapsed?: () => void; compact?: boolean;
}> = ({ s, sch, onElapsed, compact }) => {
  if (!s) {
    return <div className="text-xs text-grayscale-1100">{sch.loading ? 'Working out the next send…'
      : sch.error ? `The next send could not be worked out: ${sch.error}` : 'No schedule for this trigger.'}</div>;
  }
  const iv = sch.scheduler.interval;
  return (
    <div className="flex flex-col gap-2">
      {s.when && <div className="text-xs text-grayscale-1300"><b>When:</b> {s.when}</div>}
      {s.kind === 'external' || s.kind === 'alias' ? null : !s.auto_possible ? (
        <div className="text-xs text-grayscale-1100">{s.never_reason}</div>
      ) : (
        <div className={`rounded-lg border px-3 py-2 flex flex-wrap items-center gap-x-4 gap-y-1 ${s.live
          ? 'border-red-200 bg-red-50' : 'border-grayscale-300 bg-grayscale-100'}`}>
          {s.next_at ? (
            <>
              <div className="flex items-baseline gap-2">
                <span className={`text-xs ${s.live ? 'text-red-700 font-semibold' : 'text-grayscale-1100 italic'}`}>
                  {s.live ? 'Sends automatically in' : 'If enabled: next send in'}
                </span>
                <Countdown at={s.next_at} offsetMs={sch.offsetMs} onElapsed={onElapsed}
                  className={`text-lg ${s.live ? 'text-red-700 font-bold' : 'text-grayscale-1100'}`} />
              </div>
              <div className={`text-xs ${s.live ? 'text-red-800' : 'text-grayscale-1100'}`}>
                {fmtIST(s.next_at)} IST{peopleText(s) ? ` · ${peopleText(s)}` : ''}
                {s.runs_needed ? ` · about ${s.runs_needed} runs` : ''}
              </div>
              {s.conditional && <Pill tone="warn">only if new data arrives</Pill>}
              {!s.due_count_exact && s.due_count !== null && !s.conditional && (
                <Pill tone="mute" title="Counted before exclusions and caps (the server ran out of time for an exact count)">estimate</Pill>
              )}
            </>
          ) : (
            <div className="text-xs text-grayscale-1100 flex flex-wrap items-baseline gap-2">
              <span>{s.basis === 'event' ? 'Nobody is due yet: it fires when someone does it.' : 'Nobody is due in the time ahead.'}</span>
              {s.next_tick_at && <span>Next MEMAC run in the window: <Countdown at={s.next_tick_at} offsetMs={sch.offsetMs} />
                {' '}({fmtIST(s.next_tick_at)} IST)</span>}
            </div>
          )}
        </div>
      )}
      {s.note && <div className="text-[11px] text-grayscale-1100">{s.note}</div>}
      {s.auto_possible && s.gates.length > 0 && (
        <div className="flex flex-wrap gap-x-3 gap-y-1 text-[11px]">
          {s.gates.map(g => (
            <span key={g.key} className={g.open ? 'text-green-700' : 'text-red-700'}
              title={g.open ? `Open: ${g.label}` : `Needed: ${g.label}`}>
              {g.open ? `✓ ${g.label}` : `✕ ${g.closed.charAt(0).toUpperCase()}${g.closed.slice(1)}`}
            </span>
          ))}
        </div>
      )}
      {!compact && s.auto_possible && (
        <div className="text-[10px] text-grayscale-900">
          {s.live ? 'Every gate is open: this is a real countdown.'
            : `Not sending on its own: ${s.blocked_by.join('; ') || 'a gate is closed'}. The time is when it would go if they were open.`}
          {' '}{s.estimated_ticks
            ? `Run times are estimated (the scheduler is not running on this server): after MEMAC_AUTO=1 and a restart, the first run comes ${sch.scheduler.startup_delay ?? 5} min after the restart, then every ${iv ?? '?'} min.`
            : `The scheduler's next run is ${fmtIST(sch.scheduler.next_run_at)} IST, then every ${iv ?? '?'} min (each run starts an interval after the last one ends).`}
          {' '}Counted on today's data with the dry run's rules.
        </div>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// MEMAC → Templates: each built-in e-mail's live timing (the card's "When" is
// the seeded wording; an admin can change a trigger's timing).
// ---------------------------------------------------------------------------
export const TemplateTimings: React.FC<{
  triggers: Trigger[]; sch: Schedules; onOpenTrigger: (id: string) => void; onElapsed?: () => void;
}> = ({ triggers, sch, onOpenTrigger, onElapsed }) => {
  const rows = triggers.filter(t => !t.external && !t.alias_of && t.content_mode === 'template' && t.template_key);
  if (!rows.length) return null;
  return (
    <details className="rounded-lg border border-grayscale-300 bg-white px-3 py-2">
      <summary className="text-xs font-semibold text-grayscale-1300 cursor-pointer select-none">
        When each of these e-mails goes out — live, from its trigger ({rows.length})
      </summary>
      <p className="text-[11px] text-grayscale-1100 mt-1">
        A card's “When” below is the timing as seeded. This list follows each trigger's current settings and counts down
        to its next automatic send (“if enabled” while a gate is closed).
      </p>
      <div className="overflow-x-auto mt-2">
        <table className="w-full text-xs">
          <thead>
            <tr className="text-left text-[10px] uppercase tracking-wide text-grayscale-1100 border-b border-grayscale-200">
              <th className="py-1 pr-3">Template</th><th className="py-1 pr-3">Trigger</th>
              <th className="py-1 pr-3">When</th><th className="py-1 pr-3">Next automatic send</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(t => {
              const s = sch.byId[t.id];
              return (
                <tr key={t.id} className="border-b border-grayscale-100 align-top">
                  <td className="py-1.5 pr-3"><div>{t.template_name || t.template_key}</div>
                    <code className="text-[10px] text-grayscale-1100">{t.template_key}</code></td>
                  <td className="py-1.5 pr-3 whitespace-nowrap">
                    <button type="button" className="text-primary-700 hover:underline font-mono" onClick={() => onOpenTrigger(t.id)}>{t.id}</button>
                  </td>
                  <td className="py-1.5 pr-3 text-grayscale-1200 min-w-[16rem]">{s?.when || (sch.loading ? '…' : '—')}</td>
                  <td className="py-1.5 pr-3"><NextSendCell s={s} offsetMs={sch.offsetMs} loading={sch.loading} onElapsed={onElapsed} /></td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </details>
  );
};
